import { Stack, Typography } from '@mui/material'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import DialogButton from '../../Common/Buttons/DialogButton'
import Dialog from '../../Common/Dialog/Dialog'
import DialogActions from '../../Common/Dialog/DialogActions'
import DialogContent from '../../Common/Dialog/DialogContent'
import DialogTitle from '../../Common/Dialog/DialogTitle'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { BatchRemoveFromCollection } from '../../UserData/_actions/CollectionActions'
import { type BatchProductDesignSelection } from '../../UserData/_actions/ProductDesignActions'
import { type UserProductDesignStore } from '../../UserData/_stores/UserProductDesignStore'
import { ClearProductDesignSelectedProduct } from '../../UIData/_actions/Account/AccountStoreActions'

type Props = {
  opened: boolean
  collectionId: number
  collectionName: string
  onClose: () => void
} & ({
  productDesign: UserProductDesignStore
  selection?: never
  selectionCount?: never
} | {
  productDesign?: never
  selection: BatchProductDesignSelection
  selectionCount: number
})

export default function RemoveFromCollectionDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()
  const loader = useAppSelector(state => state.get('UIData').get('loaders').get('edit_product_designs'))

  const onConfirm = useCallback(() => {
    const selection = props.selection ?? {
      ids: [props.productDesign.get('id')],
    }

    dispatch(BatchRemoveFromCollection(props.collectionId, selection).set({
      onSuccess: () => {
        if (props.selection) {
          dispatch(ClearProductDesignSelectedProduct())
        }

        props.onClose()
      }
    }))
  }, [dispatch, props])

  return <Dialog
    open={props.opened}
    maxWidth="xs"
    fullWidth
    onClose={props.onClose}
    showCloseButton
  >
    <DialogTitle>{props.selection
      ? t('Remove {{count}} products from {{collection}}', { count: props.selectionCount, collection: props.collectionName })
      : t('Remove {{name}} from {{collection}}', { name: props.productDesign.get('name'), collection: props.collectionName })}</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body1">{props.selection
          ? t('This will remove the selected products from this collection. The products will remain in your account.')
          : t('This will remove this product from the collection. The product will remain in your account.')}</Typography>
      </Stack>
    </DialogContent>
    <DialogActions>
      <DialogButton
        color="secondary"
        disabled={!!loader}
        onClick={props.onClose}
      >{t('Cancel')}</DialogButton>
      <DialogButton
        color="primary"
        disabled={!!loader}
        onClick={onConfirm}
        autoFocus
      >{t('Remove')}</DialogButton>
    </DialogActions>
  </Dialog>
}
